import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { defaultStringifySearch } from "@tanstack/router-core";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/trpc/client";
import { getRedirectResult, signOut } from "firebase/auth";
import { auth } from "@/firebase/app";
import { type AuthState, authStore, initAuth } from "@/store/auth";
import {
  beginSignupProvisioning,
  endSignupProvisioning,
} from "@/lib/signupProvisioning";
import { publishAuthRedirectEmail } from "@/lib/authEmulatorRedirect";
import { initAutoHideScrollbars } from "@/lib/autoHideScrollbars";
import { NotFoundPage, ServerErrorPage } from "#/components/ErrorPage";
import {
  getGtmContainerId,
  gtmBootstrapScript,
  pushVirtualPageView,
} from "@/lib/gtm";
import { GOODSENDER_ICON_LINKS } from "@/lib/brandIcons";

import { ToastLayer } from "@/ui";

import "../styles.css";
// Loaded here, not in `AppShell`, because the `--gs-*` tokens are not the
// dashboard's alone: every `@/ui` primitive reads them, and onboarding renders
// outside the shell (see `OnboardingStep`). Imported by `AppShell` it only
// reached the document once a dashboard route had rendered, so a cold load
// straight onto `/onboarding/*` — the first screen after signup, and any
// refresh — resolved every token to nothing: the `hero` heading fell back to
// 16px body text and `Button variant="primary"` lost its accent fill, leaving
// Continue and Skip identical. After `styles.css`, which is the order
// `AppShell` already produced and which theme.css's own rules assume when they
// outrank it.
import "@/ui/theme.css";
// After theme.css: the marketing layer reads its tokens and must outrank it
// the same way theme.css outranks styles.css.
import "../styles/site.css";

export interface RouterContext {
  auth: AuthState;
}

// Targeted by the inline loader script in `head()`'s `scripts` entry below.
const TEXT_FONT_LINK_ID = "gs-text-font-css";
// Shared with the <noscript> fallback in RootDocument, below, so a
// script-less visitor to a public SSR route still gets the font (#1074/US4).
const TEXT_FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=Manrope:wght@400;500;600;700;800&family=Space+Grotesk:wght@400;500;700&family=Inter:wght@400;500;700&display=swap";

export const Route = createRootRouteWithContext<RouterContext>()({
  // The root must opt in for any route to SSR: a parent with `ssr: false`
  // forces every descendant to false, and without this flag the root falls
  // back to `defaultSsr: false` (src/start.ts), disabling SSR app-wide.
  // Client-only routes still send shell-only HTML — they default to false
  // themselves (D-216-5).
  ssr: true,
  head: ({ matches }) => ({
    // NOTE: charset is intentionally NOT declared here. It's rendered as the
    // first child of <head> in RootDocument so it lands early in the SSR byte
    // stream; declaring it here too would emit a second, later <meta charset>
    // via <HeadContent /> (multiple charset declarations are invalid HTML).
    meta: [
      {
        name: "viewport",
        content:
          "width=device-width, initial-scale=1.0, interactive-widget=resizes-visual",
      },
      { title: "GoodSender" },
    ],
    links: [
      // Omitted on a `/blog*` or `/preview*` match: `src/lib/blog/seo.ts`
      // supplies the publication's own favicon links (or this same
      // GOODSENDER_ICON_LINKS fallback when the blog has no logo) for those
      // routes, and TanStack Router concatenates `links` across every matched
      // route with no dedupe — emitting both sets here let GoodSender's SVG
      // favicon outrank a publication's icon in browsers that prefer SVG
      // (#1099 / US2, bug 2).
      ...(matches?.some(
        (m) => m.routeId.startsWith("/blog") || m.routeId.startsWith("/preview"),
      )
        ? []
        : GOODSENDER_ICON_LINKS),
      // Pay DNS+TCP+TLS to the font origins up front so the stylesheet/font
      // fetches below don't each start from a cold connection (#415,
      // PageSpeed "network dependency tree"). gstatic serves the font
      // *binaries* cross-origin → crossOrigin required; googleapis serves the
      // CSS and must NOT be crossOrigin.
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      // Text families for the marketing/app UI. Loaded here as a <link> rather
      // than a top-of-styles.css `@import` so it is not chained behind
      // styles.css's download+parse (#415). Same URL + display=swap as before,
      // so the rendered fonts are unchanged.
      //
      // Non-blocking (#1074/US4), same preload-plus-deferred-stylesheet
      // mechanism as the themes' `FONTS` (fieldnote.ts/laneful.ts): a
      // `rel="preload" as="style"` warms the fetch, and `media: "print"`
      // means the browser never applies (or blocks on) the stylesheet link
      // during initial render. React cannot serialize an `onLoad` handler
      // into SSR output, so there is no JSX equivalent of the themes'
      // `onload="this.media='all'"` — the `scripts` entry below does the
      // same flip via an inline script that targets this `id` once the
      // stylesheet has actually loaded.
      //
      // This is the only external stylesheet. The Hack (jsDelivr) and
      // Material Symbols (Google Fonts) links that used to follow it were
      // render-blocking on every route and had no consumer left: Hack went
      // with the suspension banner's v1 styling, and no element ever carried
      // the `material-symbols-outlined` class (icons are lucide-react). On
      // PageSpeed's slow-4G mobile profile each blocked first paint for
      // ~750 ms — the same mechanism #1074/A4 kept the icon font blocking to
      // avoid a ligature flash, for an icon font nothing rendered. Guarded by
      // `src/__tests__/root-head.test.tsx`.
      { rel: "preload", as: "style", href: TEXT_FONT_HREF },
      {
        id: TEXT_FONT_LINK_ID,
        rel: "stylesheet",
        media: "print",
        href: TEXT_FONT_HREF,
      },
    ],
    // Flips the text-font link above from `media="print"` to `"all"` once it
    // has actually loaded. A `<link onLoad>` prop is the natural way to write
    // this, but React does not serialize `onLoad` into SSR'd HTML — a plain
    // inline script is the SSR-safe equivalent (#1074/US4).
    //
    // Installing the handler via `l.onload = …` alone races the load: this
    // script runs wherever TanStack orders head scripts (after `links`), and
    // a cached stylesheet — or a streamed-HTML response where this chunk
    // flushes after the link's — can finish loading before that assignment
    // runs, firing `load` with no listener attached and leaving `media`
    // stuck on `"print"` forever (FR-013). `link.sheet` is non-null once the
    // resource has been fetched and parsed regardless of whether its `media`
    // currently matches, so checking it first covers the already-loaded case
    // synchronously; `addEventListener` (not a second `onload` write) covers
    // the still-pending one.
    // Font activation depends on root SSR emitting this script through HeadContent.
    scripts: [
      {
        id: `${TEXT_FONT_LINK_ID}-loader`,
        children: `(function(){var l=document.getElementById(${JSON.stringify(TEXT_FONT_LINK_ID)});if(!l)return;var a=function(){l.media="all";};if(l.sheet){a();}else{l.addEventListener("load",a);}})();`,
      },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: ServerErrorPage,
});

/**
 * Sends `virtual_page_view` on client-side route changes; first paint relies on GTM/GA4 default page_view.
 *
 * Rendered AFTER head-sync components and pushed via setTimeout(0) so any
 * sibling/route effect that updates document.title for the new path has already
 * run by the time we read it.
 */
function GtmVirtualPageviews() {
  const location = useRouterState({ select: (s) => s.location });
  const prevRef = useRef<string | null>(null);

  useEffect(() => {
    const qs = defaultStringifySearch(
      location.search as Record<string, unknown>,
    );
    const path = `${location.pathname}${qs}`;
    if (prevRef.current === null) {
      prevRef.current = path;
      return;
    }
    if (prevRef.current === path) return;
    prevRef.current = path;
    const timer = setTimeout(() => {
      pushVirtualPageView({
        pagePath: path,
        pageLocation: window.location.href,
        pageTitle: document.title,
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  return null;
}

/**
 * Client-only bootstrap for Sentry + Firebase auth, restoring what the
 * deleted `src/main.tsx` used to do before Start's `StartClient` took over
 * hydration. `StartClient` hydrates with the same router instance that
 * `useRouter()` returns here, so we can update its `context.auth` in place
 * instead of needing a custom client entry file.
 *
 * Mirrors `main.tsx`'s original sequence exactly:
 *  1. `initSentry()` — no-op during SSR / without a DSN (guarded internally).
 *     The SDK is behind a dynamic import (landing mobile PageSpeed): as a
 *     static import it was ~8% of the client entry chunk, preloaded at High
 *     priority ahead of first paint on every route, for something that only
 *     matters once JS is running. Errors thrown in the few hundred ms before
 *     the chunk lands are not reported — the same window `ErrorPage` already
 *     accepts by lazy-loading `@sentry/react` for the same reason.
 *  2. `initAuth()` resolves once Firebase's first `onAuthStateChanged` fires;
 *     push that initial state into the router context + Sentry scope (store
 *     subscriptions don't fire on registration, so this can't be skipped).
 *  3. Subscribe to `authStore` to keep the router context + Sentry user in
 *     sync on every subsequent auth change, and redirect a signed-out user
 *     away from `/dashboard` — same guard `dashboard.tsx`'s `beforeLoad` uses.
 */
function AuthAndSentryBootstrap() {
  const router = useRouter();

  useEffect(() => {
    // Resolved once, to `null` if the chunk cannot be fetched (a deploy
    // rotating hashes under an open tab): error reporting is best-effort, and
    // a missing SDK must not turn every auth-state change below into an
    // unhandled rejection.
    const sentry = import("@/lib/sentry").catch(() => null);
    void sentry.then((m) => m?.initSentry());
    const setSentryUser = (uid: string | null) =>
      void sentry.then((m) => m?.setSentryUser(uid));

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    void initAuth().then(async () => {
      if (cancelled) return;

      // Cursor's built-in browser returns here from the Firebase Auth emulator
      // redirect. Raise the signup barrier before invalidate, or a new account
      // is bounced to `/`.
      if (import.meta.env.DEV && import.meta.env.VITE_USE_AUTH_EMULATOR !== "false") {
        try {
          const result = await getRedirectResult(auth);
          const email = result?.user.email ?? null;
          if (result?.user && !email) {
            await signOut(auth);
          } else if (email) {
            beginSignupProvisioning();
            publishAuthRedirectEmail(email);
          }
        } catch (err) {
          endSignupProvisioning();
          console.error("Login failed:", err);
        }
      }
      if (cancelled) return;

      let lastPushed = authStore.state;
      router.update({ context: { auth: lastPushed } });
      void router.invalidate();
      setSentryUser(lastPushed.user?.uid ?? null);

      const subscription = authStore.subscribe(() => {
        // This callback runs synchronously inside the store's notification
        // flush. Two rules keep it from freezing the tab (an unbounded
        // notify → invalidate → store-write → notify recursion fired on
        // every sign-in):
        //  1. Bail unless the auth state actually changed — re-entrant
        //     notifications for the same state must be no-ops.
        //  2. Never touch router *navigation/loading* synchronously from in
        //     here — invalidate()/navigate() write the router's own store,
        //     which re-enters the active flush. Defer them to a microtask.
        const state = authStore.state;
        if (state === lastPushed) return;
        lastPushed = state;

        router.update({ context: { auth: state } });
        setSentryUser(state.user?.uid ?? null);

        queueMicrotask(() => {
          if (cancelled || authStore.state !== state) return;
          void router.invalidate();

          if (state.isLoading) return;
          const pathname = router.state.location.pathname;
          if (!state.user && pathname.startsWith("/dashboard")) {
            void router.navigate({ to: "/login" });
          }
        });
      });
      unsubscribe = () => subscription.unsubscribe();
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [router]);

  return null;
}


/**
 * Swaps this platform's permanent scrollbars for ones that fade out when you
 * stop scrolling — see `lib/autoHideScrollbars.ts` for the mechanism and why
 * it deliberately no-ops where scrollbars already fade.
 *
 * Mounted at the root, outside the shell, because it is not the dashboard's:
 * the landing page, onboarding and the marketing routes all scroll too. It
 * measures layout, so it can only run on the client — this effect is that
 * client boundary.
 */
function AutoHideScrollbars() {
  useEffect(() => initAutoHideScrollbars(), []);
  return null;
}

function RootComponent() {
  return (
    <RootDocument>
      <QueryClientProvider client={queryClient}>
        <AutoHideScrollbars />
        <AuthAndSentryBootstrap />
        <GtmVirtualPageviews />
        <Outlet />
        {/*
          Carries its own `data-modal-inert-exempt` wrapper, which is what
          keeps toasts alive while a dialog inerts the rest of the page — see
          src/ui/ToastLayer.tsx. Mounted here, as a direct child of <body>.
        */}
        <ToastLayer />
        {/*
          The @tanstack/devtools-vite plugin (devtools() in vite.config.ts)
          strips this <TanStackDevtools> subtree from production builds, so it
          ships only in dev — do NOT wrap it in an `import.meta.env.DEV &&`
          guard: the plugin removes the element, leaving `{cond && }`, which is
          a syntax error that breaks the prod build (#415).
        */}
        <TanStackDevtools
          config={{
            position: "bottom-right",
          }}
          plugins={[
            {
              name: "TanStack Router",
              render: <TanStackRouterDevtoolsPanel />,
            },
          ]}
        />
      </QueryClientProvider>
    </RootDocument>
  );
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  const gtmId = getGtmContainerId();

  return (
    <html lang="en" className="dark" data-theme="dark">
      <head>
        {/*
          Charset must appear within the first 1024 bytes of the document and
          before any content it could affect. Rendering it as the first <head>
          child (rather than via head()'s meta array, which <HeadContent />
          emits below, after the theme-color metas and the inline GTM script)
          keeps it first in the SSR output. It's the single source of truth —
          head() deliberately omits charSet.
        */}
        <meta charSet="utf-8" />
        {/*
          One value, because there is one palette. This was a light/dark pair
          (#f5f5f5 / #0a0a0a) back when the marketing site and the blog followed
          the OS colour scheme; both are warm and light in every scheme now, so
          the dark half painted the browser's chrome near-black above a paper
          page. `#f7f4ed` is `--gs-bg`, the ground every public surface opens on.

          Collapsing the pair also retires the reason this had to be static JSX:
          TanStack Router's head-merge dedups meta entries by name/property and
          ignores `media`, so two entries sharing name="theme-color" collided and
          only one survived into <head>. With a single entry there is nothing to
          collide with — it stays here rather than moving into head()'s meta
          array only so it keeps its place near the top of the SSR byte stream,
          alongside charset.
        */}
        <meta name="theme-color" content="#f7f4ed" />
        {/*
          GTM bootstrap script, rendered as static JSX for the same reason as
          the theme-color metas above: `vite.config.ts`'s old `gtmPlugin` used
          Vite's `transformIndexHtml` hook to inject this, but that hook only
          fires when transforming a literal `index.html` — which was removed
          when this app migrated to Start/Nitro, silently making the plugin
          dead code. Injecting it here as JSX works for both SSR and CSR.
        */}
        {/*
          Preconnect to GTM's origin only when GTM is actually configured
          (prod — apphosting.prod.yaml sets VITE_GTM_ID; dev omits it), matching
          the bootstrap-script gate below so dev never opens a wasted connection
          (#415).
        */}
        {gtmId && (
          <link rel="preconnect" href="https://www.googletagmanager.com" />
        )}
        {gtmId && (
          <script
            dangerouslySetInnerHTML={{
              __html: gtmBootstrapScript(gtmId),
            }}
          />
        )}
        <HeadContent />
        {/*
          The text-font link above ships `media="print"` and is only flipped to
          `"all"` by the inline loader script in head()'s `scripts` entry — so a
          visitor with JS disabled (or blocked) would otherwise never apply it,
          even though this route SSRs the full page for exactly that visitor.
          The themed blog templates (fieldnote.ts/laneful.ts) guard the same
          mechanism with an identical <noscript> fallback (#1074/US4).
        */}
        <noscript>
          <link rel="stylesheet" href={TEXT_FONT_HREF} />
        </noscript>
      </head>
      <body>
        {gtmId && (
          <noscript>
            <iframe
              title="Google Tag Manager"
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            />
          </noscript>
        )}
        {children}
        <Scripts />
      </body>
    </html>
  );
}
